import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApi } from '@/lib/ConfigProvider'
import { useSession } from '@/auth/SessionProvider'
import { countriesByName } from '@/lib/countries'
import { register } from './registration'
import type {
  ConsentAcceptance,
  RegistrationField,
  RegistrationRefusal,
  SubjectKind,
} from './registration'
import { fetchConsentTexts, mayRegister, requiredTexts } from '@/privacy/consentTexts'
import { languageName } from '@/privacy/locale'
import type { ConsentText, ConsentTextsState } from '@/privacy/consentTexts'
import './Register.css'

/**
 * The step that turns a signed-in identity into a customer.
 *
 * # Why this screen asks for a country at all
 *
 * Because nothing else can. Invariant 7 makes country gating fail-closed and
 * the backend refuses to guess: `registration.go` rejects an empty country
 * rather than defaulting one, on the grounds that "a defaulted country is a
 * customer onboarded outside the mandate with no record of the guess". The
 * browser could guess — from the locale, from a timezone, from an IP — and
 * every one of those guesses would be this app taking a licensing decision on
 * someone's behalf and writing nobody's name against it.
 *
 * # The list is not an offer
 *
 * `countries.ts` carries every ISO-3166 code because there is no customer-facing
 * endpoint for which countries are open, and inventing one in a browser bundle
 * would be a copy of the registry that goes stale the first time an operator
 * opens a country. So the customer names their country, the backend answers,
 * and the copy here is careful never to imply the list means anything more.
 */
export function Register({ onRegistered }: { onRegistered: () => void | Promise<void> }) {
  const api = useApi()
  const { state: session } = useSession()
  const countries = useMemo(() => countriesByName(), [])

  // Prefilled from the identity, because the customer already gave it to Ory
  // and retyping it is friction with no purpose. Absent when the trait is not
  // an address — see `emailOf`.
  const knownEmail = session.status === 'authenticated' ? session.identity.email : undefined

  const [country, setCountry] = useState('')
  const [legalName, setLegalName] = useState('')
  // Address, asked here because the institution that issues the account
  // requires it and acceptance is too late to ask — see RegistrationInput.
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [addressCity, setAddressCity] = useState('')
  const [addressPostalCode, setAddressPostalCode] = useState('')
  const [email, setEmail] = useState(knownEmail ?? '')
  const [kind, setKind] = useState<SubjectKind>('individual')

  const [busy, setBusy] = useState(false)
  const [refusal, setRefusal] = useState<RegistrationRefusal | null>(null)

  // The wording, and which of it this person has ticked.
  //
  // Read from `GET /v1/privacy/consent-texts` and NOT from
  // `GET /v1/privacy/consents`: the second carries a customer's answers and
  // therefore requires a customer, and this screen exists to create one
  // (`go-wallet#910`). Reading the wrong one here 401s exactly the person it is
  // for, and `lib/api` turns a 401 into a session round-trip — so the mistake
  // would look like an auth problem rather than a contract one.
  const [wording, setWording] = useState<ConsentTextsState>({ status: 'loading' })
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set())

  const readWording = useCallback(async () => {
    setWording({ status: 'loading' })
    const next = await fetchConsentTexts(api)
    setWording(next)
    // EVERY TICK IS DROPPED ON A RE-READ, deliberately. A tick is a claim about
    // a specific version and hash; carrying it across a re-read would let a
    // person be recorded as accepting wording that arrived after they ticked.
    // That is the 409 the backend answers, arriving from the inside.
    setTicked(new Set())
  }, [api])

  useEffect(() => {
    void readWording()
  }, [readWording])

  // One key for the life of this screen, not one per click (invariant 5). The
  // endpoint is idempotent on the Ory subject anyway, so this is belt and
  // braces — but a key that changed per attempt would be a client asserting two
  // different intents for one, which is the habit that causes double payments
  // on routes where the backend is not idempotent.
  const [idempotencyKey] = useState(() => `register-${crypto.randomUUID()}`)

  const accepted: readonly ConsentAcceptance[] =
    wording.status === 'ready'
      ? requiredTexts(wording)
          .filter((t) => ticked.has(t.purpose))
          // The act, from the control that was used. A tick is a tick.
          .map((t) => ({ text: t, action: 'checkbox_ticked' as const }))
      : []

  const canSubmit = mayRegister(wording, ticked)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy || !canSubmit) return
    setBusy(true)
    setRefusal(null)

    const outcome = await register(api, idempotencyKey, {
      country,
      legalName: legalName.trim(),
      addressLine1: addressLine1.trim(),
      addressLine2: addressLine2.trim(),
      addressCity: addressCity.trim(),
      addressPostalCode: addressPostalCode.trim(),
      email: email.trim(),
      kind,
      accepted,
    })

    if (outcome.status === 'registered') {
      // `created: false` is success. The identity was already registered —
      // a retry, a double submit, or a signup resumed in another tab — and the
      // customer's position is the same either way. Re-read from
      // `GET /v1/onboarding` rather than assuming it.
      await onRegistered()
      // Normally unreachable: the re-read moves the screen off this component.
      // Cleared anyway, because the one case where it does NOT — the read
      // failing, or answering `not_a_customer` again — would otherwise leave a
      // permanently disabled button and a customer with nothing to press.
      setBusy(false)
      return
    }

    setBusy(false)
    setRefusal(outcome.refusal)
    // The wording moved, or was not quoted. Re-read so the person answers the
    // text that is live NOW — retrying what we sent would answer a version they
    // never saw, which is precisely what the backend refused.
    if (outcome.refusal.kind === 'consent' && outcome.refusal.reread) {
      await readWording()
    }
  }

  const fieldError = (f: RegistrationField): string | null =>
    refusal !== null && refusal.kind === 'field' && refusal.field === f ? refusal.message : null

  return (
    /*
      `noValidate` so the backend's wording is what a customer reads, in one
      voice, rather than a browser's built-in message in whatever language the
      browser is set to. The cost is a round trip on a mis-submit, which is the
      right trade: the alternative is a second copy of the validation rules
      here, and a second copy is a copy that will eventually disagree.
    */
    <form className="reg" onSubmit={(e) => void submit(e)} noValidate>
      <p className="reg__lede">
        Two things before we can open your wallet. Your legal name has to match the document you
        verify with, and we have to know which country you are in — that decides which rules apply
        to you.
      </p>

      <label className="reg__field">
        <span className="reg__label">Country</span>
        <select
          className="reg__input"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          disabled={busy}
          required
          aria-describedby="reg-country-note"
          {...(fieldError('country') === null ? {} : { 'aria-invalid': true })}
        >
          {/*
            No preselection, and the empty option is not a placeholder trick —
            it is the honest initial state. A selected first country would be a
            default, and this is the one field that must never have one.
          */}
          <option value="">Choose a country…</option>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
        <span className="reg__note" id="reg-country-note">
          Tesora is not open everywhere yet. Choosing a country here is not a sign that it is
          available — we will tell you straight away if it is not.
        </span>
        <FieldError message={fieldError('country')} />
      </label>

      <label className="reg__field">
        <span className="reg__label">Legal name</span>
        <input
          className="reg__input"
          type="text"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          disabled={busy}
          autoComplete="name"
          required
          {...(fieldError('legalName') === null ? {} : { 'aria-invalid': true })}
        />
        <span className="reg__note">As written on your passport or ID card.</span>
        <FieldError message={fieldError('legalName')} />
      </label>

      <label className="reg__field">
        <span className="reg__label">Email</span>
        <input
          className="reg__input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          autoComplete="email"
          required
          {...(fieldError('email') === null ? {} : { 'aria-invalid': true })}
        />
        <FieldError message={fieldError('email')} />
      </label>

      {/*
        ONE label per line, not a single free-text box. The institution takes a
        street and a city as separate fields and validates them separately, so a
        single box would have to be split by us — and a split that guesses is a
        guess about somebody's home written into a regulated record.

        The postal code is not marked required: several countries do not use
        one, and demanding it would refuse addresses the institution accepts.
      */}
      <label className="reg__field">
        <span className="reg__label">Street address</span>
        <input
          className="reg__input"
          type="text"
          value={addressLine1}
          onChange={(e) => setAddressLine1(e.target.value)}
          disabled={busy}
          autoComplete="address-line1"
          required
          {...(fieldError('addressLine1') === null ? {} : { 'aria-invalid': true })}
        />
        <span className="reg__note">
          The institution that issues your account needs your home address.
        </span>
        <FieldError message={fieldError('addressLine1')} />
      </label>

      <label className="reg__field">
        <span className="reg__label">Address line 2 (optional)</span>
        <input
          className="reg__input"
          type="text"
          value={addressLine2}
          onChange={(e) => setAddressLine2(e.target.value)}
          disabled={busy}
          autoComplete="address-line2"
        />
      </label>

      <label className="reg__field">
        <span className="reg__label">City</span>
        <input
          className="reg__input"
          type="text"
          value={addressCity}
          onChange={(e) => setAddressCity(e.target.value)}
          disabled={busy}
          autoComplete="address-level2"
          required
          {...(fieldError('addressCity') === null ? {} : { 'aria-invalid': true })}
        />
        <FieldError message={fieldError('addressCity')} />
      </label>

      <label className="reg__field">
        <span className="reg__label">Postal code (optional)</span>
        <input
          className="reg__input"
          type="text"
          value={addressPostalCode}
          onChange={(e) => setAddressPostalCode(e.target.value)}
          disabled={busy}
          autoComplete="postal-code"
        />
      </label>

      <fieldset className="reg__field reg__field--group">
        <legend className="reg__label">Are you opening this as</legend>
        <div className="reg__choices">
          <Choice
            name="kind"
            value="individual"
            checked={kind === 'individual'}
            onChange={setKind}
            disabled={busy}
            label="Myself"
          />
          <Choice
            name="kind"
            value="company"
            checked={kind === 'company'}
            onChange={setKind}
            disabled={busy}
            label="A company"
          />
        </div>
        <span className="reg__note">
          A company wallet needs different documents. Choosing the wrong one means starting the
          checks again.
        </span>
        <FieldError message={fieldError('kind')} />
      </fieldset>

      {/*
        A `field` refusal renders on its field — EXCEPT when it names no field.
        `malformed` is exactly that: a 400 the backend does not attribute to one
        input. Testing `kind !== 'field'` alone drops it on the floor, so the
        customer presses the button, nothing happens, and nothing says why. That
        is the failure this whole PR is about, one level down.
      */}
      {refusal !== null && (refusal.kind !== 'field' || refusal.field === undefined) && (
        <Refused refusal={refusal} />
      )}

      <Wording
        state={wording}
        ticked={ticked}
        disabled={busy}
        onToggle={(purpose, on) =>
          setTicked((prev) => {
            const next = new Set(prev)
            if (on) next.add(purpose)
            else next.delete(purpose)
            return next
          })
        }
        onRetry={() => void readWording()}
      />

      <button className="reg__cta" type="submit" disabled={busy || !canSubmit}>
        {busy ? 'Opening your wallet…' : 'Open my wallet'}
      </button>
    </form>
  )
}

function FieldError({ message }: { message: string | null }) {
  if (message === null) return null
  return (
    <span className="reg__error" role="alert">
      {message}
    </span>
  )
}

/**
 * A refusal, rendered as what the customer can do about it.
 *
 * The four cases are visibly different on purpose. `go-wallet#308` exists
 * because a registry outage was reported as a closed country, and the reason it
 * mattered is entirely about what the customer does next: one of these is worth
 * trying again in a minute and one of them never will be. A shared "something
 * went wrong" would put that bug back on this side of the wire.
 */
function Refused({ refusal }: { refusal: RegistrationRefusal }) {
  switch (refusal.kind) {
    case 'field':
      // Only reaches here when the refusal names no field — `malformed`. It is
      // a client-side fault the customer cannot diagnose, so it says what to do
      // rather than restating the backend's wording as an instruction.
      return (
        <div className="reg__refusal" role="alert">
          <p className="reg__refusalTitle">We could not read those answers.</p>
          <p className="reg__note">{refusal.message}</p>
          <p className="reg__note">Check the fields above and try again.</p>
        </div>
      )
    case 'retry':
      return (
        <div className="reg__refusal" role="alert">
          <p className="reg__refusalTitle">We could not check just now.</p>
          <p className="reg__note">{refusal.message}</p>
          <p className="reg__note">
            Nothing has been created and nothing was lost. Trying again is safe.
          </p>
        </div>
      )
    case 'closed':
      return (
        <div className="reg__refusal reg__refusal--final" role="alert">
          <p className="reg__refusalTitle">We cannot open a wallet in that country yet.</p>
          <p className="reg__note">{refusal.message}</p>
          <p className="reg__note">
            This is about where the wallet is licensed, not about you. If you chose the wrong
            country, change it above.
          </p>
          <DecisionRef refusal={refusal} />
        </div>
      )
    case 'email_taken':
      return (
        <div className="reg__refusal" role="alert">
          <p className="reg__refusalTitle">That email already has a wallet.</p>
          <p className="reg__note">{refusal.message}</p>
          <p className="reg__note">
            If it is yours, sign in with it instead. Otherwise use a different address.
          </p>
        </div>
      )
    case 'support':
      // Deliberately offers no retry and no alternative field to edit. There is
      // nothing the customer can change about this request that would make it
      // succeed (`go-wallet#302`), and a retry button here is a loop that cannot
      // terminate.
      return (
        <div className="reg__refusal reg__refusal--final" role="alert">
          <p className="reg__refusalTitle">This account needs a person to look at it.</p>
          <p className="reg__note">{refusal.message}</p>
        </div>
      )
    case 'signin':
      return (
        <div className="reg__refusal" role="alert">
          <p className="reg__refusalTitle">Your session has expired.</p>
          <p className="reg__note">Sign in again and we will pick up where you left off.</p>
        </div>
      )
    case 'consent':
      // NOT a failure, and it must not read as one. The document moved between
      // the render and the submit, no customer was created, nothing was
      // recorded — and the current wording is already back on the screen,
      // because `submit` re-read it before this rendered.
      return (
        <div className="reg__refusal" role="alert">
          <p className="reg__refusalTitle">The wording changed while this page was open.</p>
          <p className="reg__note">{refusal.message}</p>
          <p className="reg__note">
            Nothing was recorded against the version you were reading. The current wording is
            below — please read it and agree again.
          </p>
        </div>
      )
    default:
      return (
        <div className="reg__refusal" role="alert">
          <p className="reg__refusalTitle">We could not open your wallet.</p>
          <p className="reg__note">{refusal.message}</p>
          <DecisionRef refusal={refusal} />
        </div>
      )
  }
}

/**
 * Invariant 8: every refusal surfaces a `decision_ref`, and the UI must not be
 * the thing that drops it. Without it an operator cannot reconstruct why this
 * person was refused, which is the whole point of the append-only journal.
 */
function DecisionRef({ refusal }: { refusal: RegistrationRefusal }) {
  const ref = 'decisionRef' in refusal ? refusal.decisionRef : undefined
  if (ref === undefined) return null
  return (
    <p className="reg__ref">
      Reference <code>{ref}</code> — quote this if you contact us.
    </p>
  )
}

function Choice({
  name,
  value,
  checked,
  onChange,
  disabled,
  label,
}: {
  name: string
  value: SubjectKind
  checked: boolean
  onChange: (v: SubjectKind) => void
  disabled: boolean
  label: string
}) {
  return (
    <label className="reg__choice" data-checked={checked}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      <span>{label}</span>
    </label>
  )
}

/**
 * The wording, and the ticks (`#117`, backend `go-wallet#910`).
 *
 * # Every byte of legal text on this screen comes out of the response
 *
 * Nothing here composes wording, not even a placeholder. `wallet-admin consent
 * publish` reads the body from a file so the hashed bytes are the ones counsel
 * signed off; text invented in a browser bundle would not fill the gap, it
 * would produce a recorded acceptance of words nobody approved, in a table that
 * cannot be edited, with a supervisory authority as the eventual reader.
 *
 * The sentences below are ABOUT the wording — what a tick means, why we could
 * not read it — and never the wording itself.
 *
 * # The four absences are four different facts
 *
 * "Nothing is being asked of you", "this deployment does not serve it", "we
 * could not look" and "still loading" are the same empty panel and mean
 * entirely different things about whether it is safe to continue. Two of them
 * block the button; two of them do not. Collapsing them is `#22` and `#101`.
 */
function Wording({
  state,
  ticked,
  disabled,
  onToggle,
  onRetry,
}: {
  state: ConsentTextsState
  ticked: ReadonlySet<string>
  disabled: boolean
  onToggle: (purpose: string, on: boolean) => void
  onRetry: () => void
}) {
  switch (state.status) {
    case 'loading':
      return (
        <p className="reg__note" role="status">
          Reading the terms you need to agree to…
        </p>
      )

    case 'nothing_published':
      // CORRECT BEHAVIOUR, and it has to read as one. A deployment that has
      // published no wording asks for nothing and records nothing, which the
      // backend says at WARN on every registration. The button stays enabled
      // because we READ that answer — unlike the two below, where we did not.
      return (
        <p className="reg__note">
          There is nothing here for you to agree to yet. We will ask you, and record what you
          were shown, if that changes.
        </p>
      )

    case 'not_offered':
      // No retry. The contract publishes no 404 for this route, so a 404 means
      // it was never registered — a deployment state, and no number of retries
      // registers a route (the `NO_RETRY` rule from `#75`).
      return (
        <div className="reg__refusal reg__refusal--final" role="alert">
          <p className="reg__refusalTitle">We cannot show you the terms here.</p>
          <p className="reg__note">
            This deployment does not serve them, so we cannot record what you were shown — and
            we will not open a wallet without that.
          </p>
        </div>
      )

    case 'unavailable':
      return (
        <div className="reg__refusal" role="alert">
          <p className="reg__refusalTitle">We could not read the terms.</p>
          <p className="reg__note">{state.reason}</p>
          <p className="reg__note">
            Nothing has been created. We will not open a wallet until we can show you what you
            are agreeing to.
          </p>
          <button className="reg__retry" type="button" onClick={onRetry} disabled={disabled}>
            Try again
          </button>
        </div>
      )

    case 'ready':
      return (
        <section className="reg__wording" aria-labelledby="reg-wording-title">
          <h2 className="reg__wordingTitle" id="reg-wording-title">
            What you are agreeing to
          </h2>
          <p className="reg__note">
            Published in {languageName(state.locale)}. We record which version you were shown.
          </p>
          {state.texts.map((t) => (
            <Term
              key={t.purpose}
              text={t}
              ticked={ticked.has(t.purpose)}
              disabled={disabled}
              onToggle={onToggle}
            />
          ))}
        </section>
      )
  }
}

/**
 * What to say about a text this sign-up does not demand a tick for.
 *
 * This used to branch on `required` alone and say "this one is for information;
 * you do not have to agree to it" for both cases (#137). For a DISCLOSURE that
 * is exactly right. For a CHOICE it states the opposite of the truth: `choice`
 * is the field that decides whether an answer can be given at all — the backend
 * answers `409 not_a_choice` on the other kind — so a purpose with `choice:
 * true` is one the customer is entitled to decide, and calling it information
 * tells them there is nothing to decide.
 *
 * `required` and `choice` are different predicates and neither is derivable
 * from the other, which the component below already says about the tick. The
 * same is true of the sentence beside it.
 *
 * The line points at Settings rather than offering a control here because
 * that is where the answer can actually be recorded (#126). It does not
 * promise a default either way: what happens to an unanswered optional purpose
 * is the backend's to say, and this screen must not invent it.
 */
function asideFor(choice: boolean): string {
  return choice
    ? 'this one is your choice, and you can make it in Settings once your wallet is open.'
    : 'this one is for information; you do not have to agree to it.'
}

/**
 * One published text.
 *
 * A REQUIRED one gets a tick and is demanded; anything else is shown and not.
 * `required` is the SERVER'S answer — it is true for exactly the purposes the
 * registration refuses this sign-up for omitting — and it is deliberately not
 * re-derived from `choice`. The two are different predicates, and a client that
 * inferred one from the other would tick something the backend refuses.
 */
function Term({
  text,
  ticked,
  disabled,
  onToggle,
}: {
  text: ConsentText
  ticked: boolean
  disabled: boolean
  onToggle: (purpose: string, on: boolean) => void
}) {
  return (
    <article className="reg__term">
      <h3 className="reg__termTitle">{text.summary}</h3>
      {/*
        VERBATIM, and `white-space: pre-wrap` in the stylesheet rather than any
        transformation here. This is the text that was hashed: a trim, a
        re-wrap or a truncation would make the hash we echo back describe bytes
        the person did not see.
      */}
      <p className="reg__termBody">{text.body}</p>
      <p className="reg__termMeta">
        Version {text.version}
        {text.required ? '' : ` — ${asideFor(text.choice)}`}
      </p>
      {text.required ? (
        <label className="reg__termTick">
          <input
            type="checkbox"
            checked={ticked}
            disabled={disabled}
            onChange={(e) => onToggle(text.purpose, e.currentTarget.checked)}
          />
          <span>I have read this and I agree to it</span>
        </label>
      ) : null}
    </article>
  )
}
